import { createContext, useContext, useId, useMemo, type ComponentProps } from "react";
import { cx } from "../lib/cx";

/**
 * Circular progress ring (adapted from the shadcn-style `circular-progress`).
 *
 * `value` null/undefined = indeterminate: a quarter arc that spins. Pass a
 * number only when the progress is real (e.g. upload bytes); parse jobs report
 * no percentage, so their loaders stay indeterminate.
 */

const DEFAULT_MAX = 100;

type ProgressState = "indeterminate" | "complete" | "loading";

type Ctx = {
  value: number | null;
  valueText: string | undefined;
  max: number;
  min: number;
  state: ProgressState;
  radius: number;
  thickness: number;
  size: number;
  center: number;
  circumference: number;
  percentage: number | null;
  valueTextId: string;
};

const CircularProgressContext = createContext<Ctx | null>(null);

function useCtx(consumer: string): Ctx {
  const ctx = useContext(CircularProgressContext);
  if (!ctx) throw new Error(`${consumer} must be used within CircularProgress`);
  return ctx;
}

function isNum(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function defaultValueText(value: number, min: number, max: number): string {
  const pct = max === min ? 100 : ((value - min) / (max - min)) * 100;
  return `${Math.round(pct)}%`;
}

export type CircularProgressProps = ComponentProps<"div"> & {
  value?: number | null;
  getValueText?(value: number, min: number, max: number): string;
  min?: number;
  max?: number;
  size?: number;
  thickness?: number;
  label?: string;
};

export function CircularProgress({
  value: valueProp = null,
  getValueText = defaultValueText,
  min: minProp = 0,
  max: maxProp,
  size = 48,
  thickness = 4,
  label,
  className,
  children,
  ...rootProps
}: CircularProgressProps) {
  const min = isNum(minProp) ? minProp : 0;
  const rawMax = isNum(maxProp) && maxProp > 0 ? maxProp : DEFAULT_MAX;
  const max = rawMax <= min ? min + 1 : rawMax;

  if (import.meta.env.DEV && thickness >= size) {
    console.warn(`CircularProgress: thickness (${thickness}) should be less than size (${size}).`);
  }

  // Out-of-range numbers clamp; anything else is indeterminate.
  const value = isNum(valueProp) ? Math.min(max, Math.max(min, valueProp)) : null;
  const valueText = value != null ? getValueText(value, min, max) : undefined;
  const state: ProgressState = value == null ? "indeterminate" : value === max ? "complete" : "loading";
  const radius = Math.max(0, (size - thickness) / 2);
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = value != null ? (max === min ? 1 : (value - min) / (max - min)) : null;

  const labelId = useId();
  const valueTextId = useId();

  const ctx = useMemo<Ctx>(
    () => ({
      value,
      valueText,
      max,
      min,
      state,
      radius,
      thickness,
      size,
      center,
      circumference,
      percentage,
      valueTextId,
    }),
    [value, valueText, max, min, state, radius, thickness, size, center, circumference, percentage, valueTextId],
  );

  return (
    <CircularProgressContext.Provider value={ctx}>
      <div
        role="progressbar"
        aria-describedby={valueText ? valueTextId : undefined}
        aria-labelledby={label ? labelId : undefined}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={value ?? undefined}
        aria-valuetext={valueText}
        data-state={state}
        {...rootProps}
        className={cx("relative inline-flex w-fit items-center justify-center", className)}
      >
        {children}
        {label ? <div id={labelId}>{label}</div> : null}
      </div>
    </CircularProgressContext.Provider>
  );
}

export function CircularProgressIndicator({ className, ...props }: ComponentProps<"svg">) {
  const ctx = useCtx("CircularProgressIndicator");
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${ctx.size} ${ctx.size}`}
      data-state={ctx.state}
      width={ctx.size}
      height={ctx.size}
      {...props}
      className={cx("-rotate-90", className)}
    />
  );
}

export function CircularProgressTrack({ className, ...props }: ComponentProps<"circle">) {
  const ctx = useCtx("CircularProgressTrack");
  return (
    <circle
      data-state={ctx.state}
      cx={ctx.center}
      cy={ctx.center}
      r={ctx.radius}
      fill="none"
      stroke="currentColor"
      strokeWidth={ctx.thickness}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      {...props}
      className={cx("text-grey-100", className)}
    />
  );
}

export function CircularProgressRange({ className, ...props }: ComponentProps<"circle">) {
  const ctx = useCtx("CircularProgressRange");
  const offset =
    ctx.state === "indeterminate"
      ? ctx.circumference * 0.75
      : ctx.circumference - (ctx.percentage ?? 0) * ctx.circumference;
  return (
    <circle
      data-state={ctx.state}
      cx={ctx.center}
      cy={ctx.center}
      r={ctx.radius}
      fill="none"
      stroke="currentColor"
      strokeWidth={ctx.thickness}
      strokeLinecap="round"
      strokeDasharray={ctx.circumference}
      strokeDashoffset={offset}
      vectorEffect="non-scaling-stroke"
      {...props}
      className={cx(
        "origin-center text-primary transition-all duration-300 ease-in-out",
        ctx.state === "indeterminate" && "motion-safe:animate-spin-around motion-reduce:animate-none",
        className,
      )}
    />
  );
}

export function CircularProgressValueText({ className, children, ...props }: ComponentProps<"span">) {
  const ctx = useCtx("CircularProgressValueText");
  return (
    <span
      id={ctx.valueTextId}
      data-state={ctx.state}
      {...props}
      className={cx("absolute inset-0 flex items-center justify-center text-sm font-medium", className)}
    >
      {children ?? ctx.valueText}
    </span>
  );
}

/** Ring + track + arc, with the percentage in the middle when `value` is set. */
export function CircularProgressCombined(props: CircularProgressProps) {
  return (
    <CircularProgress {...props}>
      <CircularProgressIndicator>
        <CircularProgressTrack />
        <CircularProgressRange />
      </CircularProgressIndicator>
      <CircularProgressValueText />
    </CircularProgress>
  );
}
